import { authClient, useSession } from "@/lib/auth-client";
import { Sentry } from "@/lib/sentry";
import { trpc } from "@/lib/trpc";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Bell, Check, CheckCircle2, Circle, Link2, Mail, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

/**
 * Bust Better-Auth's 5-min session cookieCache so the next _app loader read
 * sees the fresh onboardedAt. Returns void — caller always proceeds to the
 * fallback redirect since the TTL-expiry path works regardless.
 *
 * Exported for direct unit-test coverage of the Sentry-capture path (AP-002).
 */
export async function bustOnboardingCookieCache(): Promise<void> {
	try {
		await authClient.getSession({
			query: { disableCookieCache: true },
		});
	} catch (err) {
		Sentry.captureException(err, {
			tags: { area: "onboarding", step: "cookie-cache-bust" },
		});
		// Fall through — the 5-min cache TTL will expire and the
		// window.location.href reload path still works regardless.
	}
}

export const Route = createFileRoute("/_app/onboarding")({
	component: OnboardingPage,
});

type StepKey = "email" | "two-factor" | "notifications" | "quickbooks";

function OnboardingPage() {
	const { data: session } = useSession();
	const isAdmin = session?.user?.role === "admin";

	const status = trpc.profile.onboardingStatus.useQuery();
	const qboStatus = trpc.qbo.status.useQuery(undefined, {
		// Non-admins can't connect QBO — skip the query to avoid extra load.
		enabled: isAdmin,
	});

	const utils = trpc.useUtils();
	const complete = trpc.profile.completeOnboarding.useMutation({
		onSuccess: async () => {
			utils.profile.onboardingStatus.invalidate();
			toast.success("You're all set. Welcome!");
			// Bust Better-Auth's 5-min session cookieCache so the _app loader
			// reads the fresh onboardedAt on the next page load — otherwise
			// the stale cached session keeps bouncing us back here.
			await bustOnboardingCookieCache();
			window.location.href = "/";
		},
		onError: (err) => toast.error(err.message),
	});

	if (status.isPending) {
		return (
			<div className="mx-auto max-w-2xl space-y-6">
				<div className="h-8 w-64 animate-pulse rounded bg-[#f3f4f6]" />
				<div className="h-40 w-full animate-pulse rounded bg-[#f3f4f6]" />
			</div>
		);
	}

	const s = status.data ?? {
		emailVerified: false,
		twoFactorEnabled: false,
		notificationPrefsReviewed: false,
		onboarded: false,
	};

	const qboConnected = qboStatus.data?.connected === true;

	const steps: StepDefinition[] = [
		{
			key: "email",
			icon: <Mail className="h-5 w-5" strokeWidth={1.5} />,
			title: "Verify your email",
			description: s.emailVerified
				? "Verified — you used your invite link."
				: "Check your inbox for the verification link.",
			done: s.emailVerified,
			required: true,
			action: null,
		},
		{
			key: "two-factor",
			icon: <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />,
			title: "Enroll two-factor authentication",
			description: s.twoFactorEnabled
				? "Enrolled. You'll enter a 6-digit code each time you sign in."
				: "Set up an authenticator app. Email codes + backup codes work as fallbacks.",
			done: s.twoFactorEnabled,
			required: true,
			action: s.twoFactorEnabled ? null : { to: "/settings/security", label: "Set up 2FA" },
		},
		{
			key: "notifications",
			icon: <Bell className="h-5 w-5" strokeWidth={1.5} />,
			title: "Review notification preferences",
			description: s.notificationPrefsReviewed
				? "Saved. You can change these anytime in Settings."
				: "Choose what you want to be emailed about — invoices, extraction failures, QBO events.",
			done: s.notificationPrefsReviewed,
			required: true,
			action: s.notificationPrefsReviewed
				? null
				: { to: "/settings/notifications", label: "Review prefs" },
		},
	];

	if (isAdmin) {
		steps.push({
			key: "quickbooks",
			icon: <Link2 className="h-5 w-5" strokeWidth={1.5} />,
			title: "Connect QuickBooks Online",
			description: qboConnected
				? `Connected to ${qboStatus.data?.companyName ?? "your QBO company"}.`
				: "Optional — lets you push invoices straight from PayrollMart. You can do this later.",
			done: qboConnected,
			required: false,
			action: qboConnected ? null : { to: "/settings", label: "Connect" },
		});
	}

	const requiredDone = steps.filter((s) => s.required).every((s) => s.done);
	const totalDone = steps.filter((s) => s.done).length;

	return (
		<div className="mx-auto max-w-2xl space-y-6">
			<div>
				<h1 className="text-2xl font-semibold text-[#1e1e20]">Welcome to PayrollMart</h1>
				<p className="mt-1 text-sm text-[#6b7280]">
					A few quick steps to finish setting up your account. Complete the required ones to start
					using the app.
				</p>
			</div>

			<div className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-4 py-3">
				<div className="h-2 flex-1 overflow-hidden rounded-full bg-[#f3f4f6]">
					<div
						className="h-full rounded-full bg-[#16a34a] transition-all"
						style={{ width: `${(totalDone / steps.length) * 100}%` }}
					/>
				</div>
				<span className="text-xs font-medium text-[#6b7280]">
					{totalDone} / {steps.length} complete
				</span>
			</div>

			<ul className="space-y-3">
				{steps.map((step) => (
					<StepCard key={step.key} step={step} />
				))}
			</ul>

			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<button
					type="button"
					onClick={() => complete.mutate()}
					disabled={!requiredDone || complete.isPending}
					className="h-10 w-full rounded bg-[#16a34a] text-sm font-semibold text-white transition-colors hover:bg-[#15803d] disabled:opacity-50"
				>
					{complete.isPending ? "Finishing up…" : "Complete setup"}
				</button>
				{!requiredDone && (
					<p className="mt-2 text-center text-xs text-[#6b7280]">
						Finish the required steps above to continue.
					</p>
				)}
			</div>
		</div>
	);
}

// ─── Step card ─────────────────────────────────────────────────────────

type StepDefinition = {
	key: StepKey;
	icon: ReactNode;
	title: string;
	description: string;
	done: boolean;
	required: boolean;
	action: {
		to: "/settings/security" | "/settings/notifications" | "/settings";
		label: string;
	} | null;
};

function StepCard({ step }: { step: StepDefinition }) {
	return (
		<li
			className={`flex items-start gap-4 rounded-lg border p-5 ${
				step.done ? "border-[#d1fae5] bg-[#f0fdf4]" : "border-[#e5e7eb] bg-white"
			}`}
		>
			<div
				className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
					step.done ? "bg-[#16a34a] text-white" : "bg-[#f3f4f6] text-[#6b7280]"
				}`}
			>
				{step.done ? <Check className="h-5 w-5" strokeWidth={2} /> : step.icon}
			</div>
			<div className="flex-1">
				<div className="flex items-center gap-2">
					<h3 className="text-sm font-semibold text-[#1e1e20]">{step.title}</h3>
					{!step.required && (
						<span className="rounded bg-[#f3f4f6] px-1.5 py-0.5 text-xs font-medium text-[#6b7280]">
							Optional
						</span>
					)}
				</div>
				<p className="mt-1 text-sm text-[#6b7280]">{step.description}</p>
			</div>
			<div className="flex-shrink-0">
				{step.done ? (
					<CheckCircle2 className="h-5 w-5 text-[#16a34a]" strokeWidth={1.5} />
				) : step.action ? (
					<Link
						to={step.action.to}
						className="inline-flex h-8 items-center rounded bg-[#16a34a] px-3 text-xs font-semibold text-white hover:bg-[#15803d]"
					>
						{step.action.label}
					</Link>
				) : (
					<Circle className="h-5 w-5 text-[#d1d5db]" strokeWidth={1.5} />
				)}
			</div>
		</li>
	);
}
